<template>
    <div>
        <div class="mt-5">
            <div class="mb-3 text-right">
                <hts-form-button
                    v-if="checkPermission(layoutPermissions.company_user_add)"
                    icon-name="hi-plus-circle-icon"
                    icon-position="right"
                    type="submit"
                    @click="openAddCompanyUser">
                    {{ $t('labels.addUser') }}
                </hts-form-button>
            </div>
        </div>
        <div class="body">
            <hts-data-table
                ref="dataTables"
                :headers="companyUserHeaders"
                :server="server"
                :use-archive="useCompanyUserArchive"
                use-selection
                store-name="companyUserDataTable"
                store-key="CompanyUser" />
        </div>
        <add-company-user
            ref="addCompanyUser"
            :list-company-group="groupList"
            :list-company-user="listCompanyUser"
            @refresh-tables="refreshTables" />
        <edit-company-user
            :show="showSlideOverEditCompanyUser"
            :list-company-group="groupList"
            :company-user-is-selected-to-edit="companyUserIsSelectedToEdit"
            @refresh-tables="refreshTables()"
            @close="showSlideOverEditCompanyUser = false" />
        <hts-modal
            :show="showPopupDeleteCompanyUser"
            :title="$t('labels.deleteCompanyUser')"
            type="confirm"
            :content="$t('labels.confirmDeleteCompanyUser')"
            :loading="deleting"
            @close="closePopupDeleteCompanyUser"
            @confirm="handleDeleteCompanyUser" />
        <hts-modal
            :show="showPopupResendMailCompanyUser"
            :title="$t('labels.resendEmail')"
            type="confirm"
            :content="$t('labels.confirmResendEmail')"
            @hide="closePopupResendMailCompanyUser"
            @confirm="handleResendMailCompanyUser" />
        <hts-modal
            :show="showPopupChangeStatusCompanyUser"
            :title="$t('labels.status')"
            type="confirm"
            :content="$t('validations.confirmStatus')"
            @confirm="actionChangeStatusCompanyUser" />
    </div>
</template>

<script setup lang="ts">
import Actions from './-components/actions.vue'
import AddCompanyUser from './-components/add-company-user.vue'
import EditCompanyUser from './-components/edit-company-user.vue'
import { ICompanyUser } from './-components/imports/form'
import UserStatus from './-components/user-status.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'

const route = useRoute()
const dataTables = ref()
const server = ref({})
onBeforeMount(() => {
  server.value = $url.company.companyUserGetList
  getGroup()
  getCompanyUserList()
})
const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const openDelete = (user: any) => {
  if (!user) {
    return null
  }
  showPopupDeleteCompanyUser.value = true
  companyUserId.value = user._id
}
const openEdit = (user: any) => {
  if (!user) {
    return null
  }
  handleEditCompanyUser(user)
}
const addCompanyUser: any = ref(null)
const openAddCompanyUser = () => {
  if (addCompanyUser.value) {
    addCompanyUser.value.open()
  }
}

const useCompanyUserArchive = {
  api: $url.company.companyUserArchive,
  permissions: checkPermission(layoutPermissions.company_user_archive)
}

const companyUserHeaders = [
  {
    field: 'fullName',
    label: 'fullName',
    headerName: $t('labels.fullName'),
    sortable: true
  },
  {
    field: 'address',
    label: 'address',
    headerName: $t('labels.address'),
    sortable: true
  },
  {
    field: 'contactEmail',
    label: 'contactEmail',
    headerName: $t('labels.emailRegister'),
    valueGetter: (params: any) => {
      if (!params.data) {
        return null
      }
      const contactEmail = params.data?.contactEmail
      const registerBy = params.data?.registerBy

      if (!contactEmail) {
        return null
      }
      return contactEmail + (registerBy === 'email' ? ' *' : '')
    }
  },
  {
    field: 'phoneRegister',
    label: 'phoneRegister',
    headerName: $t('labels.phoneRegister'),
    valueGetter: (params: any) => {
      if (!params.data) {
        return null
      }
      const code = params.data?.phone?.code
      const phone = params.data?.phone?.phone
      const registerBy = params.data?.registerBy

      if (!code || !phone) {
        return null
      }
      return code + phone + (registerBy === 'phone' ? ' *' : '')
    }
  },
  {
    field: 'jobTitle',
    label: 'jobTitle',
    headerName: $t('labels.jobTitle'),
    sortable: true
  },
  {
    field: 'group',
    label: 'group',
    headerName: $t('labels.companyGroup'),
    sortable: true,
    valueGetter: (params: any) => {
      if (!params.data) {
        return null
      }
      const group = groupList.value.find((item: any) => item._id === params.data.group)
      if (!group) {
        return ''
      }
      return group.name || ''
    }
  },
  {
    field: 'status',
    label: 'status',
    headerName: $t('labels.status'),
    valueSetter: (params: any) => {
      valueStatus.value = params.newValue
      dataChangeStatus.value = params.data
      showPopupChangeStatusCompanyUser.value = true
    },
    autoHeight: true,
    wrapText: true,
    cellRenderer: UserStatus
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    suppressMenu: true,
    suppressMovable: true,
    resizable: false,
    headerName: $t('labels.action'),
    headerClass: 'ag-header-text-center',
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'companyUserDataTable',
      storeName: 'CompanyUser',
      items: [
        {
          icon: 'hi-pencil-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.company_user_edit),
          click: openEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.company_user_delete),
          isArchive: true,
          click: openDelete
        }
      ]
    }
  }
]

const showSlideOverAddCompanyUser = ref(false)
const companyUserIsSelectedToDelete = reactive<ICompanyUser>({
  workspace: '',
  user: '',
  status: 0,
  company: '',
  group: '',
  fullName: '',
  address: '',
  email: '',
  jobTitle: ''
})
const companyUserIsSelectedToResendMail = reactive<ICompanyUser>({
  workspace: '',
  user: '',
  status: 0,
  company: '',
  group: '',
  fullName: '',
  address: '',
  email: '',
  jobTitle: ''
})
const showPopupDeleteCompanyUser = ref(false)
const deleting = ref(false)
const showPopupResendMailCompanyUser = ref(false)
const showSlideOverEditCompanyUser = ref(false)
const showPopupChangeStatusCompanyUser = ref(false)
const companyUserIsSelectedToEdit = reactive<ICompanyUser>({
  workspace: '',
  user: '',
  status: 0,
  company: '',
  group: '',
  fullName: '',
  address: '',
  email: '',
  jobTitle: ''
})
const companyUserId = ref('')
const emailCompanyUser = ref()
const valueStatus = ref()
const dataChangeStatus = ref()
const listCompanyUser = ref<ICompanyUser[]>([])
const handleResendMailCompanyUser = (companyUser: any) => {
  const api = {
    url: '/company/user/resendMail',
    method: 'post'
  }
  $api(api, { body: new URLSearchParams({ email: emailCompanyUser.value }) })
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        showPopupResendMailCompanyUser.value = false
        return true
      }
      if (responseData.success) {
        refreshTables()
        showPopupResendMailCompanyUser.value = false
        process.client && $toast().success($t('labels.resendEmailSuccess'))
      }
    })
    .catch(() => {
      process.client && $toast().error($t('labels.canNotConnectServer'))
    })
}
const handleDeleteCompanyUser = () => {
  const api = {
    url: '/company/user/' + companyUserId.value,
    method: 'delete'
  }
  deleting.value = true
  $api(api)
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      if (responseData.success) {
        refreshTables()
        process.client && $toast().success($t('labels.deleteCompanyUserSuccess'))
        showPopupDeleteCompanyUser.value = false
      }
      deleting.value = false
    })
    .catch(() => {
      deleting.value = false
    })
}

const actionChangeStatusCompanyUser = () => {
  const api = {
    url: '/company/user/change-status',
    method: 'post'
  }
  const data = {
    status: valueStatus.value,
    id: dataChangeStatus.value._id
  }
  $api(api, { body: { ...data } })
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      if (responseData.success) {
        refreshTables()
        process.client && $toast().success($t('labels.updateStatusSuccess'))
        showPopupChangeStatusCompanyUser.value = false
      }
    })
    .catch(() => {
      process.client && $toast().error($t('labels.canNotConnectServer'))
    })
}

const closePopupDeleteCompanyUser = () => {
  showPopupDeleteCompanyUser.value = false
  Object.assign(companyUserIsSelectedToDelete, {})
}
const closePopupResendMailCompanyUser = () => {
  showPopupResendMailCompanyUser.value = false
  Object.assign(companyUserIsSelectedToResendMail, {})
}

const handleEditCompanyUser = (companyUser: { [key: string]: any }) => {
  Object.assign(companyUserIsSelectedToEdit, {
    ...companyUser,
    email: companyUser.contactEmail
  })
  showSlideOverEditCompanyUser.value = true
}
const groupList = ref<Array<{ [key: string]: any }>>([])
const getGroup = () => {
  $api($url.companyGroup.getAllGroup)
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      groupList.value = responseData.data.rows
    })
    .catch((error: any) => error)
}
const getCompanyUserList = () => {
  $api($url.company.companyUserGetList)
    .then((data: any) => {
      if (!data) {
        return true
      }
      const responseData: any = data.data.value
      if (!responseData) {
        return true
      }
      listCompanyUser.value = responseData.data.rows
    })
    .catch((error: any) => error)
}
</script>
